/**
 * Conversation practice API — /api/backoffice/conversations y /api/conversations (estudiante).
 */

import { ApiClient, buildQuery } from './client';
import type {
  ConversationPractice,
  ConversationPracticeFormData,
  ConversationPracticeListItem,
  ContentItem,
  ConversationSituationDocument,
} from '@/types/conversation.types';
import type { BackgroundImageVersion } from '@/types/backoffice.types';
import type { Activity, UserAnswer } from '@/types/story.types';

export async function getBackofficeConversationPractices(filters?: {
  searchQuery?: string;
  level?: string;
  status?: string;
}): Promise<{ resources: ConversationPracticeListItem[]; total: number }> {
  const query = buildQuery({
    search: filters?.searchQuery,
    level: filters?.level,
    status: filters?.status,
  });
  return ApiClient.get<{ resources: ConversationPracticeListItem[]; total: number }>(
    `/backoffice/conversations${query}`
  );
}

export async function getBackofficeConversationPracticeById(
  id: string
): Promise<ConversationPractice | null> {
  try {
    return await ApiClient.get<ConversationPractice>(`/backoffice/conversations/${id}`);
  } catch {
    return null;
  }
}

/** Crea o actualiza práctica de conversación (contentType implícito en servidor). */
export async function saveConversationPractice(
  id: string | null,
  data: ConversationPracticeFormData
): Promise<ConversationPracticeListItem> {
  const body: Record<string, unknown> = {
    title: data.title,
    description: data.description,
    level: data.level,
    status: data.status,
    estimatedTime: data.estimatedTime,
    xpReward: data.xpReward,
  };
  if (id !== null && data.backgroundImage !== undefined) {
    body.backgroundImage = data.backgroundImage;
  }
  if (id) {
    return ApiClient.patch<ConversationPracticeListItem>(`/backoffice/conversations/${id}`, body);
  }
  return ApiClient.post<ConversationPracticeListItem>('/backoffice/conversations', body);
}

export async function deleteConversationPractice(id: string): Promise<void> {
  await ApiClient.delete(`/backoffice/conversations/${id}`);
}

export async function getConversationPracticeContent(id: string): Promise<ContentItem[]> {
  const data = await ApiClient.get<ContentItem[]>(`/backoffice/conversations/${id}/content`);
  return Array.isArray(data) ? data : [];
}

export async function saveConversationPracticeContent(
  id: string,
  contentData: Partial<ContentItem> & { id?: string }
): Promise<ContentItem> {
  if (contentData.id) {
    return ApiClient.patch<ContentItem>(
      `/backoffice/conversations/${id}/content/${contentData.id}`,
      contentData
    );
  }
  return ApiClient.post<ContentItem>(`/backoffice/conversations/${id}/content`, contentData);
}

export async function deleteConversationPracticeContent(
  id: string,
  contentId: string
): Promise<void> {
  await ApiClient.delete(`/backoffice/conversations/${id}/content/${contentId}`);
}

/**
 * Respuesta del PUT/regenerate de audios. `missingAudios` lista identificadores
 * (`bif-N`, `bif-N-opt-A`) cuya generación TTS falló — el cliente puede mostrar
 * un aviso y permitir reintentar con `regenerateConversationPracticeAudios`.
 */
export interface ConversationPracticeAudioStats {
  totalLines: number;
  processedLines: number;
}

export interface ConversationSituationSaveResult extends ContentItem {
  /** Identificadores de líneas NPC sin audio (vacío = todo OK). */
  missingAudios?: string[];
  /** Estadísticas de la pasada de audio. `null` si la generación se omitió. */
  audioStats?: ConversationPracticeAudioStats | null;
}

export async function putConversationPracticeSituation(
  id: string,
  body: {
    document: ConversationSituationDocument;
    contentItemId?: string | null;
  }
): Promise<ConversationSituationSaveResult> {
  return ApiClient.put(`/backoffice/conversations/${id}/conversation-situation`, body);
}

export interface AuditConversationSituationResponse {
  document: ConversationSituationDocument;
  errors: string[];
  changed: boolean;
}

/**
 * Pide al backend una pasada correctiva on-demand sobre un documento de
 * conversación. Reaprovecha `runAuditPass` del pipeline de generación.
 */
export async function auditConversationSituation(
  document: ConversationSituationDocument
): Promise<AuditConversationSituationResponse> {
  return ApiClient.post<AuditConversationSituationResponse>(
    '/backoffice/audit-conversation-situation',
    { document }
  );
}

/** Regenera los audios NPC pendientes (idempotente: solo procesa los que faltan). */
export async function regenerateConversationPracticeAudios(
  id: string
): Promise<{
  success: boolean;
  totalLines: number;
  processedLines: number;
  missingAudios: string[];
}> {
  return ApiClient.post(`/backoffice/conversations/${id}/regenerate-audios`, {});
}

export interface RegenerateTranslationsResult {
  success: boolean;
  skipped: boolean;
  keysRequested: number;
  keysFilled: number;
  forceAll: boolean;
  message?: string;
}

/** Genera traducciones ES faltantes, o todas si `force: true` (sobrescribe). */
export async function regenerateConversationPracticeTranslations(
  id: string,
  options?: { force?: boolean }
): Promise<RegenerateTranslationsResult> {
  return ApiClient.post(`/backoffice/conversations/${id}/regenerate-translations`, {
    force: options?.force === true,
  });
}

export async function generateConversationPracticeBackgroundImage(id: string): Promise<{
  success: boolean;
  imageUrl: string;
  version: number;
  history: BackgroundImageVersion[];
}> {
  return ApiClient.post(`/backoffice/conversations/${id}/generate-background-image`, {});
}

export async function getConversationPracticeActivities(id: string): Promise<Activity[]> {
  const data = await ApiClient.get<Activity[]>(`/backoffice/conversations/${id}/activities`);
  return Array.isArray(data) ? data : [];
}

export async function saveConversationPracticeActivity(
  id: string,
  activityId: string | null,
  activityData: Partial<Activity>
): Promise<Activity> {
  if (activityId) {
    return ApiClient.patch<Activity>(
      `/backoffice/conversations/${id}/activities/${activityId}`,
      activityData
    );
  }
  return ApiClient.post<Activity>(`/backoffice/conversations/${id}/activities`, activityData);
}

export async function deleteConversationPracticeActivity(
  id: string,
  activityId: string
): Promise<void> {
  await ApiClient.delete(`/backoffice/conversations/${id}/activities/${activityId}`);
}

export async function reorderConversationPracticeActivity(
  id: string,
  activityId: string,
  newOrder: number
): Promise<void> {
  await ApiClient.patch(`/backoffice/conversations/${id}/activities/${activityId}/reorder`, {
    order: newOrder,
  });
}

export async function getPublishedConversationPractices(filters?: {
  level?: string;
  /** 'interest' = categorías de interés del alumno; 'other' = el resto. */
  scope?: 'interest' | 'other';
  limit?: number;
  offset?: number;
}): Promise<{ resources: ConversationPracticeListItem[]; hasMore?: boolean; total?: number }> {
  const query = buildQuery({
    level: filters?.level,
    scope: filters?.scope,
    limit: filters?.limit != null ? String(filters.limit) : undefined,
    offset: filters?.offset != null ? String(filters.offset) : undefined,
  });
  return ApiClient.get<{
    resources: ConversationPracticeListItem[];
    hasMore?: boolean;
    total?: number;
  }>(`/conversations${query}`);
}

/**
 * Conversaciones ASIGNADAS al alumno (modelo espejo de las historias). Sustituye
 * al feed en vivo: la pantalla muestra solo lo asignado (meta semanal + backlog),
 * paginado por sección. Misma forma de respuesta que getPublishedConversationPractices.
 */
export async function getUserConversationAssignments(filters?: {
  /** 'interest' = categorías de interés del alumno; 'other' = el resto. */
  scope?: 'interest' | 'other';
  limit?: number;
  offset?: number;
}): Promise<{ resources: ConversationPracticeListItem[]; hasMore?: boolean; total?: number }> {
  const query = buildQuery({
    scope: filters?.scope,
    limit: filters?.limit != null ? String(filters.limit) : undefined,
    offset: filters?.offset != null ? String(filters.offset) : undefined,
  });
  return ApiClient.get<{
    resources: ConversationPracticeListItem[];
    hasMore?: boolean;
    total?: number;
  }>(`/conversations/assignments${query}`);
}

export async function getPublishedConversationPracticeById(
  id: string
): Promise<ConversationPractice | null> {
  try {
    return await ApiClient.get<ConversationPractice>(`/conversations/${id}`);
  } catch {
    return null;
  }
}

export interface ConversationSubmitResult {
  resourceId: string;
  userId: string;
  score: number;
  totalPoints: number;
  earnedPoints: number;
  xpEarned: number;
  /** Valor del intento (piso + precisión) antes de la resta aditiva del wallet. */
  xpFull: number;
  timeSpent: number;
  activityResults: Array<{
    activityId: string;
    isCorrect: boolean;
    pointsEarned: number;
    timeSpent: number;
    userAnswer: unknown;
    correctAnswer?: unknown;
    explanation?: string;
  }>;
  completedAt: number;
  passed: boolean;
  isRetry?: boolean;
  previousBestScore?: number;
}

/**
 * Precisión del chat: turnos de decisión (`totalTurns`) y cuántos se acertaron
 * a la primera, sin reintento (`firstTryCorrect`). El backend la usa para la
 * nota (F/S×100) y el XP proporcional. Ausente en conversaciones con
 * actividades (esas se puntúan con el corrector).
 */
export interface ConversationChatAccuracy {
  totalTurns: number;
  firstTryCorrect: number;
}

export async function submitConversationActivities(
  id: string,
  data: { answers: UserAnswer[]; timeSpent: number; accuracy?: ConversationChatAccuracy }
): Promise<ConversationSubmitResult> {
  return ApiClient.post<ConversationSubmitResult>(`/conversations/${id}/submit`, {
    answers: data.answers,
    timeSpent: data.timeSpent,
    ...(data.accuracy
      ? {
          totalTurns: data.accuracy.totalTurns,
          firstTryCorrect: data.accuracy.firstTryCorrect,
        }
      : {}),
  });
}
